/**
 * Whose hub JWT is this window allowed to present? (card `t_68d110ee`)
 *
 * Measured on the tablet while proving a local connect to a LAN hub (Rožna S1,
 * `192.168.1.6:16161`): the hub accepted the device credential the device holds
 * (`GET /api/devices/status-values` → 200, `POST /api/auth/device/refresh` →
 * 200) and answered `401 {"error":"Invalid token"}` to a token it had not signed
 * — while the app's *first authenticated read* answered 401 and its recovery
 * (`POST /api/auth/refresh`) never left the WebView. The credential was on the
 * device; the app presented the wrong one.
 *
 * The rule this module writes down is the one the codebase already applies to
 * device credentials ("a key that cannot say which hub it belongs to must not
 * answer for it", `services/deviceCredential.ts`), applied to the local hub JWT:
 *
 *   - a token stored under THIS window's key for THIS hub is this connection's;
 *   - a token read from the unscoped `accessToken` key is **not** — that key holds
 *     whatever hub last signed in, in ANY window (Electron tabs share
 *     localStorage, which is why the per-hub key exists at all), and a hub JWT
 *     carries no hub claim of its own, so nothing about the token can attribute
 *     it. It answers only after the hub it is pointed at has accepted it
 *     (migration), which is what keeps long-lived installs signed in;
 *   - a token this window stored for a *different* hub (the window switched hubs
 *     after it was read — an adoption, a join, a manual connect) is not this
 *     connection's either, and presenting it is the 401 above.
 *
 * Pure: no storage, no pinia, no browser. The store supplies what it read.
 */

/** Where the token this window holds came from. */
export type LocalHubTokenSource =
  /** `hubAccessToken:<host:port>` — written by a connect in THIS window. */
  | 'window'
  /** The unscoped `accessToken` key — the last hub any window signed in to. */
  | 'legacy'
  /** No local hub token at all. */
  | 'none';

export interface BootLocalTokenInput {
  /** `hubAccessToken:<this window's hub>`: this window's own copy, if it has one. */
  windowToken: string | null;
  /** The unscoped `accessToken` key. */
  legacyToken: string | null;
  /** This window knows which hub it is on (it has its own per-window hub marker). */
  connectionKnown: boolean;
}

export interface BootLocalTokenDecision {
  token: string | null;
  source: LocalHubTokenSource;
  /**
   * The token came from the unscoped key: the hub it is pointed at has to accept
   * it before it may answer for this connection.
   */
  needsHubConfirmation: boolean;
  reason: string;
}

/**
 * Which token the store boots with, and what it is evidence for.
 *
 * The window's own key wins outright — it was written by a connect in this
 * window, for this hub. The unscoped key is still adopted (a build older than
 * the per-hub key wrote only that, and dropping it would sign those installs
 * out), but it is flagged: it answers nothing until the hub confirms it.
 */
export function selectBootLocalToken(input: BootLocalTokenInput): BootLocalTokenDecision {
  if (input.windowToken) {
    return {
      token: input.windowToken,
      source: 'window',
      needsHubConfirmation: false,
      reason: "this window's own token for this hub",
    };
  }
  if (input.legacyToken) {
    return {
      token: input.legacyToken,
      source: 'legacy',
      needsHubConfirmation: true,
      reason: input.connectionKnown
        ? 'from the unscoped key, which belongs to whichever hub last signed in — the hub this window is on must accept it first'
        : 'from the unscoped key, with no hub pinned in this window yet — the hub must accept it before it answers',
    };
  }
  return { token: null, source: 'none', needsHubConfirmation: false, reason: 'no local hub token stored' };
}

export interface ConnectionTokenInput {
  /** The session token the store is holding. */
  sessionToken: string | null;
  /** `hubAccessToken:<the hub this window is on right now>`. */
  windowToken: string | null;
  /** Where `sessionToken` came from. */
  source: LocalHubTokenSource;
  /** The hub accepted a token that was inherited from the unscoped key. */
  inheritedConfirmed: boolean;
  /** This window knows which hub it is on (its own per-window hub marker). */
  connectionKnown: boolean;
}

export interface ConnectionTokenDecision {
  /** The token to present, or null when this connection has none of its own. */
  token: string | null;
  reason: string;
}

/**
 * May the session token go out on a request to the connection in use?
 *
 * `null` is a real answer, and the honest one: the api layer then presents the
 * credential this connection actually holds — the enrolled device credential the
 * hub accepts — instead of a token issued by another hub (which is a 401, and
 * one the app used to convert into "Your session has expired. Please sign in
 * again.").
 */
export function selectConnectionUserToken(input: ConnectionTokenInput): ConnectionTokenDecision {
  const token = input.sessionToken;
  if (!token) {
    return { token: null, reason: 'no local hub token' };
  }

  if (input.source === 'window') {
    if (input.windowToken === token) {
      return { token, reason: "this window's own token for this hub" };
    }
    if (!input.connectionKnown) {
      // Nothing pins this window to a hub, so there is nothing to contradict:
      // keep the historical behaviour rather than signing the window out.
      return { token, reason: 'stored for this window; no hub pinned to contradict it' };
    }
    return {
      token: null,
      reason: 'this window has since moved to another hub — the token belongs to the previous connection',
    };
  }

  // `legacy`: the unscoped key. It answers only once a hub has accepted it.
  if (input.inheritedConfirmed) {
    if (input.windowToken === null || input.windowToken === token) {
      return { token, reason: 'inherited token the hub accepted (migrated)' };
    }
    return {
      token: null,
      reason: 'inherited token, but this window holds a different token for this hub',
    };
  }
  return {
    token: null,
    reason: 'inherited from the unscoped key and not confirmed by this hub yet',
  };
}
